<script setup lang="ts">
  definePageMeta({ hasHero: true })

  const { t, locale, tmResolved } = useResolvedI18n()
  const localePath = useLocalePath()

  const slideImages = [
    '/images/slideshow/image-1.jpg',
    '/images/slideshow/image-2.jpg',
    '/images/slideshow/image-3.jpg',
    '/images/slideshow/image-4.jpg',
    '/images/slideshow/image-5.jpg',
    '/images/slideshow/image-6.jpg',
  ]

  const slides = computed(() => {
    const items = tmResolved<Array<{ wordmark: string; tagline: string; ctaLabel: string }>>('home.slides')
    const paths = ['/admission', '/programs', '/admission']
    return slideImages.map((image, i) => {
      const s = items[i % items.length]!
      const path = paths[i % paths.length]!
      return {
        image,
        wordmark: s.wordmark,
        tagline: s.tagline,
        align: (image.includes('image-2') ? 'center' : 'bottom-left') as 'center' | 'bottom-left',
        cta: { to: localePath(path), label: s.ctaLabel },
      }
    })
  })

  const heroPreloadImage = slideImages[0]!

  useHead({
    title: () => t('home.title'),
    link: [
      {
        rel: 'preload',
        as: 'image',
        href: heroPreloadImage,
        fetchpriority: 'high',
      },
    ],
  })

  const stats = computed(() => [
    { target: 2, suffix: t('home.stats.suffix0'), label: t('home.stats.label0') },
    { target: 150, suffix: t('home.stats.suffix1'), label: t('home.stats.label1') },
    { target: 70, suffix: t('home.stats.suffix2'), label: t('home.stats.label2') },
    { target: 20, suffix: t('home.stats.suffix3'), label: t('home.stats.label3') },
  ])

  const statsRow = ref<HTMLElement | null>(null)
  const statsStarted = ref(false)
  const collision = ref(false)
  const counterTexts = ref(stats.value.map((s) => `0${s.suffix}`))

  function resetCounterTexts() {
    counterTexts.value = stats.value.map((s) => (statsStarted.value ? `${s.target}${s.suffix}` : `0${s.suffix}`))
  }

  watch([locale, stats], () => {
    resetCounterTexts()
  })

  function animateCounter(index: number) {
    const stat = stats.value[index]
    if (!stat) return
    const { target, suffix } = stat
    const duration = 2000
    const step = Math.ceil(target / (duration / 16))
    let current = 0

    const tick = () => {
      current += step
      if (current >= target) {
        counterTexts.value[index] = `${target}${suffix}`
        return
      }
      counterTexts.value[index] = `${current}${suffix}`
      requestAnimationFrame(tick)
    }
    tick()
  }

  function onStatsScroll() {
    if (statsStarted.value || !statsRow.value) return
    const rect = statsRow.value.getBoundingClientRect()
    if (rect.top < window.innerHeight - 100) {
      statsStarted.value = true
      collision.value = true
      setTimeout(() => {
        stats.value.forEach((_, i) => animateCounter(i))
      }, 400)
    }
  }

  const featureItems = computed(() => {
    const items = tmResolved<Array<{ title: string; desc: string }>>('home.features.items')
    const paths = [localePath('/about'), localePath('/education') + '#pbl', localePath('/education') + '#mentor']
    const icons = ['/images/icon/spiral.png', '/images/icon/more.png', '/images/icon/flow.png']
    return items.map((item, i) => ({
      ...item,
      to: paths[i]!,
      icon: icons[i]!,
    }))
  })

  const transItems = computed(() => tmResolved<string[]>('home.trans.items'))

  const programTagsAd = computed(() => [0, 1, 2, 3, 4, 5].map((i) => t(`home.programs.ad.tags.tag${i}`)))
  const programTagsIc = computed(() => [0, 1, 2, 3, 4, 5].map((i) => t(`home.programs.ic.tags.tag${i}`)))

  const campusItems = computed(() => [
    {
      key: 'base',
      num: '01',
      image: '/images/campus-1.jpg',
      title: t('home.campus.base.title'),
      desc: t('home.campus.base.desc'),
    },
    {
      key: 'mobile',
      num: '02',
      image: '/images/campus-2.jpg',
      title: t('home.campus.mobile.title'),
      desc: t('home.campus.mobile.desc'),
    },
    {
      key: 'virtual',
      num: '03',
      image: '/images/campus-3.jpg',
      title: t('home.campus.virtual.title'),
      desc: t('home.campus.virtual.desc'),
    },
  ])

  /** 默认展开第一项；悬停切换时用 class 驱动宽度，避免 flex-grow 无法平滑插值 */
  const activeCampus = ref(0)
  function activateCampus(index: number) {
    activeCampus.value = index
  }
  function resetCampus() {
    activeCampus.value = 0
  }

  const { list: homeNews } = await usePublicNewsList({ limit: 5 })

  const featuredNews = computed(() => homeNews.value[0] || null)
  const featuredDateText = computed(() => {
    const item = featuredNews.value
    if (!item) return ''
    return `${item.dateDay} ${item.dateMonth}`.trim()
  })

  const sideNews = computed(() => {
    const cats = tmResolved<Array<{ category: string }>>('home.news.items').map((item) => item.category)
    return homeNews.value.slice(1, 5).map((item, i) => ({
      ...item,
      category: cats[i] || t('common.headline'),
      dateYm: item.dateMonth.replace('-', '/'),
    }))
  })

  onMounted(() => {
    onStatsScroll()
    window.addEventListener('scroll', onStatsScroll, { passive: true })
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', onStatsScroll)
  })
</script>

<template>
  <!-- Hero / Splash -->
  <section class="hero">
    <AppHeroCarousel :slides="slides" />
  </section>

  <section class="trans">
    <div v-for="(label, i) in transItems" :key="i" class="trans-item">
      <div class="trans-label">{{ label }}</div>
      <div class="line"></div>
    </div>
  </section>

  <!-- Features -->
  <section class="panel panel--white features-panel pb-0">
    <div class="container">
      <div class="panel-header animate-in">
        <div class="section-title">{{ t('home.features.title') }}</div>
        <div class="section-sub">{{ t('home.features.subtitle') }}</div>
      </div>
      <div class="feature-cards">
        <div v-for="(item, i) in featureItems" :key="i" class="feature-card fly-in from-bottom">
          <img class="feature-card-icon" :src="item.icon" :alt="item.title" />
          <div class="title">{{ item.title }}</div>
          <div class="desc">{{ item.desc }}</div>
          <NuxtLink :to="item.to" class="feature-card-enter" :aria-label="item.title">
            <img src="/images/icon/enter.png" alt="" />
          </NuxtLink>
        </div>
      </div>
    </div>
  </section>

  <!-- Programs Overview -->
  <section class="panel panel--fog home-programs">
    <div class="container">
      <div class="panel-header animate-in">
        <div class="section-title">{{ t('home.programs.title') }}</div>
        <div class="section-sub">{{ t('home.programs.subtitle') }}</div>
      </div>
      <div class="major-cards">
        <article class="major-card fly-in from-left">
          <div class="major-card-media" style="background-image: url('/images/intellect-1.jpg')">
            <div class="major-card-caption">
              <h3>{{ t('home.programs.ad.title') }}</h3>
              <p>{{ t('home.programs.ad.subtitleEn') }}</p>
            </div>
          </div>
          <div class="major-card-detail">
            <div class="major-card-detail-inner">
              <h4>{{ t('home.programs.goalsLabel') }}</h4>
              <p>{{ t('home.programs.ad.goals') }}</p>
              <h4>{{ t('home.programs.directionsLabel') }}</h4>
              <div class="major-card-tags">
                <span v-for="tag in programTagsAd" :key="tag" class="major-tag">{{ tag }}</span>
              </div>
              <NuxtLink :to="localePath('/programs') + '#ad'" class="major-card-btn">
                {{ t('common.learnMore') }}
              </NuxtLink>
            </div>
          </div>
        </article>

        <article class="major-card fly-in from-right">
          <div class="major-card-media" style="background-image: url('/images/intellect-2.jpg')">
            <div class="major-card-caption">
              <h3>{{ t('home.programs.ic.title') }}</h3>
              <p>{{ t('home.programs.ic.subtitleEn') }}</p>
            </div>
          </div>
          <div class="major-card-detail">
            <div class="major-card-detail-inner">
              <h4>{{ t('home.programs.goalsLabel') }}</h4>
              <p>{{ t('home.programs.ic.goals') }}</p>
              <h4>{{ t('home.programs.directionsLabel') }}</h4>
              <div class="major-card-tags">
                <span v-for="tag in programTagsIc" :key="tag" class="major-tag">{{ tag }}</span>
              </div>
              <NuxtLink :to="localePath('/programs') + '#ic'" class="major-card-btn">
                {{ t('common.learnMore') }}
              </NuxtLink>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>

  <!-- News -->
  <section class="panel panel--white home-news">
    <div class="container">
      <div class="panel-header animate-in">
        <div class="section-title cursor-pointer" @click="navigateTo(localePath('/news'))">
          {{ t('home.news.title') }}
        </div>
        <div class="section-sub">{{ t('home.news.subtitle') }}</div>
      </div>
      <div v-if="featuredNews" class="home-news-grid">
        <article class="home-news-feature fly-in from-left">
          <div class="home-news-feature-media">
            <img :src="featuredNews.cover" :alt="featuredNews.title" referrerpolicy="no-referrer" />
            <span class="home-news-badge">{{ t('common.headline') }}</span>
          </div>
          <div class="home-news-feature-body">
            <h3>
              <NuxtLink :to="localePath(`/news/${featuredNews.slug}`)">
                {{ featuredNews.title }}
              </NuxtLink>
            </h3>
            <p>{{ featuredNews.excerpt }}</p>
            <div class="home-news-feature-date">{{ featuredDateText }}</div>
          </div>
        </article>

        <div class="home-news-side">
          <article v-for="item in sideNews" :key="item.slug" class="home-news-side-item fly-in from-right">
            <div class="home-news-side-content">
              <span class="home-news-cat">{{ item.category }}</span>
              <h4>
                <NuxtLink :to="localePath(`/news/${item.slug}`)">{{ item.title }}</NuxtLink>
              </h4>
              <p>{{ item.excerpt }}</p>
            </div>
            <div class="home-news-side-date">
              <div class="day">{{ item.dateDay }}</div>
              <div class="ym">{{ item.dateYm }}</div>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>

  <!-- Three Campus -->
  <section class="panel home-campus" style="--campus-bg: url('/images/campus-bg.jpg')">
    <div class="container">
      <div class="panel-header animate-in">
        <div class="section-title text-[var(--text-primary)]">{{ t('home.campus.title') }}</div>
        <div class="section-sub text-[var(--text-secondary)]">{{ t('home.campus.subtitle') }}</div>
      </div>
      <div class="home-campus-grid" :class="`is-active-${activeCampus}`" @mouseleave="resetCampus">
        <article
          v-for="(item, i) in campusItems"
          :key="item.key"
          class="home-campus-card fly-in"
          :class="i === 0 ? 'from-left delay-1' : i === 1 ? 'from-bottom delay-2' : 'from-right delay-3'"
          :style="{ backgroundImage: `url('${item.image}')` }"
          @mouseenter="activateCampus(i)"
        >
          <div class="home-campus-mask" aria-hidden="true" />
          <div class="home-campus-shade" aria-hidden="true" />
          <div class="home-campus-body">
            <span class="home-campus-num">{{ item.num }}</span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </article>
      </div>
    </div>
  </section>

  <!-- CTA / 招生简章 -->
  <section class="home-cta">
    <div class="container home-cta-inner">
      <h2 class="home-cta-title">{{ t('home.cta.slogan') }}</h2>
      <p class="home-cta-sub">{{ t('home.cta.sub') }}</p>
      <NuxtLink :to="localePath('/admission')" class="btn btn--white home-cta-btn">
        {{ t('home.cta.button') }}
      </NuxtLink>
    </div>
  </section>
</template>

<style scoped lang="scss">
  .trans {
    background-image: url('/images/trans_bg.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    justify-content: space-around;
    gap: 16px;
    min-height: 200px;
    padding: 40px 16px;

    .trans-item {
      flex: 1;
      max-width: 280px;
      text-align: center;
    }

    .trans-label {
      color: #fff;
      font-size: clamp(18px, 3.2vw, 35px);
      font-weight: 700;
      line-height: 1.3;
    }

    .line {
      width: 100%;
      height: 2px;
      margin-top: clamp(16px, 4vw, 88px);
      background-color: #fff;
    }
  }

  .section-title {
    font-size: clamp(24px, 4vw, 42px);
    font-weight: 700;
    line-height: 1.3;
  }

  .section-sub {
    font-size: clamp(15px, 2vw, 20px);
    line-height: 1.6;
    margin-top: 8px;
  }

  .home-cta {
    padding: clamp(48px, 8vw, 120px) 0;
  }

  @media (max-width: 768px) {
    .trans {
      flex-direction: column;
      min-height: 0;
      padding: 36px 16px;
      gap: 28px;
      max-width: 100%;
      box-sizing: border-box;
      overflow: hidden;
    }

    .trans-item {
      max-width: none;
      width: 100%;
      min-width: 0;
    }

    .trans-label {
      word-break: break-word;
    }

    .line {
      margin-top: 12px;
    }
  }
</style>
